import { Box, Divider, List, ListItem } from "@mui/material";
import {
  ServiceClaim,
  ServiceSummary,
  ServiceWithClaims,
} from "data/interfaces";
import { ClaimSelectionItem } from "./ClaimSelectionItem";
import {
  getClaimDescription,
  hasClaimTypeAndValue,
} from "../../data/functions";
import { ClaimSelectionItemDropdown } from "./ClaimSelectionItemDropdown";

interface MultipleClaimsSelectionProps {
  services: ServiceSummary[];
  defaultSelected: ServiceWithClaims[];
  selectedUsersCount: number;
  handleClaimSelect: (
    checked: boolean,
    service: ServiceSummary,
    claim: ServiceClaim,
    claimValue: string
  ) => void;
}

export function ClaimOptionSelection({
  services,
  defaultSelected,
  selectedUsersCount,
  handleClaimSelect,
}: MultipleClaimsSelectionProps) {
  let servicesWithDropdown = services.filter(
    (s) =>
      s.displayDropdownForMultipleOptions &&
      s.serviceClaims.some((c) => c.options.length > 1)
  );
  let servicesWithOptionDescription = services.filter((s) =>
    s.serviceClaims.find((sc) => sc.displaySingleServiceClaimOptionDescription)
  );
  let normalServices = services.filter(
    (s) =>
      !servicesWithDropdown.includes(s) &&
      !servicesWithOptionDescription.includes(s)
  );

  const userHasClaimTypeAndValue = (
    licenseReference: string,
    claimType: string,
    claimValue: string
  ): boolean => {
    const userServiceClaims = defaultSelected.find(
      (s) => s.licenseReference === licenseReference
    )?.claims;
    return userServiceClaims
      ? hasClaimTypeAndValue(userServiceClaims, claimType, claimValue)
      : false;
  };

  return (
    <>
      {normalServices.length > 0 && (
        <List sx={{ width: "100%" }}>
          {normalServices.map((service) =>
            service.serviceClaims.map((claim) =>
              claim.options.map((option) => (
                <ListItem key={option.claimValue} disablePadding>
                  <ClaimSelectionItem
                    service={service}
                    primaryText={service.name}
                    secondaryText={getClaimDescription(claim, option)}
                    selectedUsersCount={selectedUsersCount}
                    defaultChecked={userHasClaimTypeAndValue(
                      service.licenseReference,
                      claim.claimType,
                      option.claimValue
                    )}
                    handleCheckboxSelect={(checked) =>
                      handleClaimSelect(
                        checked,
                        service,
                        claim,
                        option.claimValue
                      )
                    }
                  />
                </ListItem>
              ))
            )
          )}
        </List>
      )}
      {normalServices.length > 0 &&
        (servicesWithDropdown.length > 0 ||
          servicesWithOptionDescription.length > 0) && <Divider />}
      {servicesWithDropdown.length > 0 && (
        <List sx={{ width: "100%" }}>
          {servicesWithDropdown.map((service) =>
            service.serviceClaims.map((claim) => (
              <Box key={claim.claimType}>
                <ClaimSelectionItemDropdown
                  service={service}
                  claim={claim}
                  primaryText={service.name}
                  secondaryText={claim.shortName}
                  selectedUsersCount={selectedUsersCount}
                  userHasClaimTypeAndValue={userHasClaimTypeAndValue}
                  handleCheckboxSelect={handleClaimSelect}
                />
              </Box>
            ))
          )}
        </List>
      )}
      {servicesWithDropdown.length > 0 &&
        servicesWithOptionDescription.length > 0 && <Divider />}
      {servicesWithOptionDescription.length > 0 && (
        <List sx={{ width: "100%" }}>
          {servicesWithOptionDescription.map((service) =>
            service.serviceClaims.map((claim) =>
              claim.options.map((option) => (
                <ListItem key={option.claimValue} disablePadding>
                  <ClaimSelectionItem
                    service={service}
                    primaryText={option.claimValueDescription}
                    secondaryText={null}
                    selectedUsersCount={selectedUsersCount}
                    defaultChecked={userHasClaimTypeAndValue(
                      service.licenseReference,
                      claim.claimType,
                      option.claimValue
                    )}
                    handleCheckboxSelect={(checked) =>
                      handleClaimSelect(
                        checked,
                        service,
                        claim,
                        option.claimValue
                      )
                    }
                  />
                </ListItem>
              ))
            )
          )}
        </List>
      )}
    </>
  );
}
